import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useParams } from 'react-router';

import { useCompanyById } from '@/api/activity';
import { InstantSearch, InstantSearchSortOrder } from '@/components/InstantSearch';
import { Page, PageDateRangeFilter, PageFilterProvider } from '@/components/page';
import { ReturnToLink } from '@/components/ReturnToLink';
import { exampleAIQuestions } from '@/constants/exampleAIQuestions';
import DetailPageTitle from '@/features/DetailPageTitle';
import ErrorOrChildren from '@/features/ErrorOrChildren';
import ShareMenu from '@/features/ShareMenu';
import { webTrafficActivity } from '@/router/routes/analyticsRoutes';

import CompanyDetailKPIMetrics from './CompanyDetailKPIMetrics';
import CompanyDetailOpportunitiesTable from './CompanyDetailOpportunitiesTable';
import CompanyDetailTrendComboCharts from './CompanyDetailTrendComboCharts';

const DEFAULT_QUERY_STATE = {
  page: 1,
  pageSize: 10,
  sortBy: 'openDate',
  sortOrder: InstantSearchSortOrder.DESC,
};

const CompanyDetail = () => {
  const { company: companyId } = useParams();
  const { company, isLoading, error } = useCompanyById(companyId);

  return (
    <Page
      title={t`Analytics`}
      pageName={company?.name}
      exampleAIQuestions={exampleAIQuestions('analytics')}
    >
      <div className="flex flex-col gap-6">
        <PageFilterProvider>
          <ReturnToLink route={webTrafficActivity.path} routeName={t`Web Traffic Activity`} />
          <div className="flex justify-between">
            <PageDateRangeFilter />
            <ShareMenu notificationPageKey="company" />
          </div>
          <ErrorOrChildren
            error={error}
            message404={<Trans>The company you're looking for does not exist</Trans>}
          >
            <DetailPageTitle
              title={company?.name}
              subtitleLabel={<Trans>Account name</Trans>}
              subtitle={company?.account?.name}
              isLoading={isLoading}
            />
            <CompanyDetailKPIMetrics companyId={companyId} />
            <CompanyDetailTrendComboCharts company={company} />
            <InstantSearch urlSync={false} defaultQueryState={DEFAULT_QUERY_STATE}>
              <CompanyDetailOpportunitiesTable companyId={companyId} />
            </InstantSearch>
          </ErrorOrChildren>
        </PageFilterProvider>
      </div>
    </Page>
  );
};

export default CompanyDetail;
